import React, { useMemo, useState } from "react";
import { Search, ArrowRight, X } from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/lib/api";

const BRAND_COLORS = {
  apple: "from-slate-800 to-slate-500",
  samsung: "from-blue-800 to-blue-500",
  oneplus: "from-red-700 to-red-500",
  xiaomi: "from-orange-600 to-amber-500",
  vivo: "from-indigo-700 to-blue-400",
  oppo: "from-green-700 to-emerald-500",
  realme: "from-yellow-500 to-orange-500",
  google: "from-teal-600 to-sky-500",
  motorola: "from-purple-700 to-fuchsia-500",
  nothing: "from-zinc-800 to-zinc-500",
  honor: "from-rose-700 to-pink-500",
  asus: "from-red-900 to-red-500",
  sony: "from-slate-800 to-slate-500",
  tecno: "from-cyan-700 to-cyan-400",
  infinix: "from-lime-700 to-lime-400",
  nokia: "from-blue-900 to-blue-500",
};

const BRAND_INITIALS = {
  apple: "iP", samsung: "S", oneplus: "1+", xiaomi: "Mi", vivo: "V",
  oppo: "O", realme: "r", google: "G", motorola: "M", nothing: "N",
  honor: "H", asus: "R", sony: "Xp", tecno: "T", infinix: "iF", nokia: "N",
};

export default function ModelGrid({ brand, products, loading, onPick }) {
  const [q, setQ] = useState("");
  const searching = q.trim().length > 0;

  const listBrandOnly = useMemo(() => (
    brand ? products.filter((p) => p.brand === brand) : products
  ), [products, brand]);

  const searchResults = useMemo(() => {
    if (!searching) return [];
    const query = q.trim().toLowerCase();
    // Global search across ALL brands when the user is typing
    return products
      .filter((p) => p.name.toLowerCase().includes(query))
      .slice(0, 50);
  }, [products, q, searching]);

  if (!brand && !searching) {
    // Show a compact search-only card when brand isn't picked
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-2 pb-10">
        <div className="rounded-2xl border border-primary/20 bg-primary/[0.03] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <span className="tv-chip mb-2">Or search directly</span>
            <div className="font-bold text-base sm:text-lg">Know your exact model? Skip picking a brand.</div>
            <div className="text-sm text-muted-foreground">Search across all {products.length}+ models — iPhone, Samsung, OnePlus, Pixel and more.</div>
          </div>
          <div className="relative w-full sm:w-96">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. iPhone 14, OnePlus 12, Pixel 8…"
              className="pl-9 pr-9 h-11 text-base"
              data-testid="model-search-input"
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-14">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="tv-chip mb-3">02 · Choose Model</span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tighter">
            {brand ? `Popular ${brand.charAt(0).toUpperCase() + brand.slice(1)} models` : "Search all models"}
          </h2>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={brand ? `Search all ${products.length}+ models…` : "e.g. iPhone 14, OnePlus 12…"}
            className="pl-9 pr-9 h-11 text-base"
            data-testid="model-search-input"
            autoFocus={!brand}
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground"
              data-testid="clear-search-btn"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {searching ? (
        <SearchList results={searchResults} onPick={onPick} query={q} />
      ) : loading ? (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-56 rounded-2xl bg-secondary animate-pulse" />
          ))}
        </div>
      ) : listBrandOnly.length === 0 ? (
        <div className="text-center py-14 text-muted-foreground">No models found.</div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {listBrandOnly.map((p, idx) => {
            const startPrice = Math.min(...p.storage_variants.map((v) => v.base_price));
            const maxPrice = Math.max(...p.storage_variants.map((v) => v.base_price));
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                className="group rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-lg transition-all overflow-hidden flex flex-col"
                data-testid={`model-card-${p.slug}`}
              >
                <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 relative overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 tv-chip-emerald text-[10px]">
                    Up to {formatINR(maxPrice)}
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="font-semibold leading-tight">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {p.storage_variants.length} storage variants
                  </div>
                  <div className="mt-3 text-xs font-mono uppercase tracking-widest text-muted-foreground">Starting</div>
                  <div className="font-mono text-lg font-bold text-accent">{formatINR(startPrice)}</div>
                  <Button
                    className="mt-3 w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => onPick(p)}
                    data-testid={`select-model-${p.slug}`}
                  >
                    Get Price <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Fast compact list for search — no image loads, just clean rows.
 * Renders instantly, easy to scan.
 */
function SearchList({ results, onPick, query }) {
  if (results.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm" data-testid="search-empty">
        No models matching <span className="font-mono font-semibold text-foreground">&quot;{query}&quot;</span>.
        Try a shorter search like <span className="font-mono">iPhone</span> or <span className="font-mono">Nord</span>.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm" data-testid="search-list">
      <div className="px-4 py-2 border-b border-border bg-secondary/50 text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center justify-between">
        <span>{results.length} model{results.length !== 1 ? "s" : ""} found</span>
        <span>Tap a row to price it →</span>
      </div>
      <ul className="divide-y divide-border max-h-[520px] overflow-y-auto">
        {results.map((p) => {
          const startPrice = Math.min(...p.storage_variants.map((v) => v.base_price));
          const maxPrice = Math.max(...p.storage_variants.map((v) => v.base_price));
          const initials = BRAND_INITIALS[p.brand] || p.brand.slice(0, 2).toUpperCase();
          const grad = BRAND_COLORS[p.brand] || "from-slate-700 to-slate-500";
          return (
            <li key={p.id}>
              <button
                onClick={() => onPick(p)}
                data-testid={`search-result-${p.slug}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary/5 transition-colors group"
              >
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${grad} text-white font-brand font-extrabold text-xs flex items-center justify-center shrink-0 shadow-sm`}>
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {p.storage_variants.map((v) => v.label).join(" · ")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Up to</div>
                  <div className="font-mono font-bold text-sm text-accent">{formatINR(maxPrice)}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
